import { Modal, Table, TableColumn } from '@components';
import ConsolidatedBudgetAPICaller from '@services/api/consolidatedBudget';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPercentageClass } from 'utils/number';
import { formatCurrency } from 'utils/string/currency';

interface Props {
  isOpen: boolean;
  categoryId: string;
  categoryName: string;
  month: string;
  budgetTemplateId: string;
  costCenterId: string;
  type: string;
}

interface TableData {
  id: string;
  type: 'Realizado' | 'Previsto';
  description: string;
  businessPartner: string;
  amount: number;
}

interface rawTransactionData {
  id: string;
  description: string;
  businessPartner: string;
  amount: number;
}

interface rawBudgetData {
  id: string;
  name: string;
  customer: string;
  valueCents: number;
}

interface TotalsData {
  totalBudget: number;
  totalRealized: number;
  percentageUsage: number;
}

const formatTransactionData = (
  rawData: Array<rawTransactionData>
): Array<TableData> => {
  return rawData.map(({ id, description, businessPartner, amount }) => {
    return {
      id,
      type: 'Realizado',
      description,
      businessPartner,
      amount,
    };
  });
};

const formatBudgetData = (rawData: Array<rawBudgetData>): Array<TableData> => {
  return rawData.map(({ id, name, customer, valueCents }) => {
    return {
      id,
      type: 'Previsto',
      description: name,
      businessPartner: customer,
      amount: valueCents,
    };
  });
};

const formatResponse = ({
  transactions,
  budgets,
}: {
  transactions: Array<rawTransactionData>;
  budgets: Array<rawBudgetData>;
}): Array<TableData> => {
  return [...formatBudgetData(budgets), ...formatTransactionData(transactions)];
};

const CategoryDetailModal = ({
  isOpen,
  categoryId,
  categoryName,
  month,
  budgetTemplateId,
  costCenterId,
  type,
}: Props) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Array<TableData>>([]);
  const [totalsData, setTotalsData] = useState<TotalsData>({
    totalBudget: 0,
    totalRealized: 0,
    percentageUsage: 0,
  });

  const loadCostCenterBudgetMonth = useCallback(() => {
    if (!budgetTemplateId || !costCenterId || !type || !month || !categoryId) {
      return;
    }

    setLoading(true);
    ConsolidatedBudgetAPICaller.listCostCenterBudgetMonthAndCategory({
      budgetTemplateId,
      costCenterId,
      type,
      month,
      categoryId,
    })
      .then(({ result: { transactions, budgets, totals } }) => {
        setData(formatResponse({ transactions, budgets }));
        setTotalsData(totals);
        setLoading(false);
      })
      .catch(() => {
        toast.error('Erro ao carregar o detalhe da categoria do mês.');
        navigate(-1);
      });
  }, [budgetTemplateId, categoryId, costCenterId, month, navigate, type]);

  useEffect(() => {
    loadCostCenterBudgetMonth();
  }, [loadCostCenterBudgetMonth]);

  return (
    <Modal
      isOpen={isOpen}
      onClickAway={() => navigate(-1)}
      title={categoryName}
      size="large"
      className="text-primary-20 max-height-100 overflow-y-auto"
    >
      <Table
        data={data}
        dataKey="id"
        showColumnSelector={false}
        isLoading={loading}
        colorVariant="neutral"
      >
        <TableColumn fromKey="description" header="Descrição" />
        <TableColumn fromKey="businessPartner" header="Fornecedor" />
        <TableColumn fromKey="type" header="Tipo" />
        <TableColumn
          fromKey="amount"
          header="Valor"
          headerAlign="end"
          textAlign="right"
          render={(value) => {
            return formatCurrency(value / 100);
          }}
        />
      </Table>

      <Table
        data={[totalsData]}
        showColumnSelector={false}
        isLoading={loading}
        className="mt-s-400"
      >
        <TableColumn fromKey="" header="Total" render={() => ' '} />
        <TableColumn
          fromKey="totalBudget"
          header="Previsto"
          headerAlign="end"
          textAlign="right"
          render={(value) => {
            return formatCurrency(value / 100);
          }}
        />
        <TableColumn
          fromKey="totalRealized"
          header="Realizado"
          headerAlign="end"
          textAlign="right"
          render={(value) => {
            return formatCurrency(value / 100);
          }}
        />

        <TableColumn
          fromKey="percentageUsage"
          header="Diferença"
          headerAlign="end"
          textAlign="right"
          render={(percentage) => {
            return (
              <div className={getPercentageClass(percentage, '')}>
                {Number(percentage).toFixed(2)}%
              </div>
            );
          }}
        />
      </Table>
    </Modal>
  );
};

export default CategoryDetailModal;
